import React, {createRef, RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {FeaturesCarouselSlideProps} from './FeaturesCarousel.slide';
import styled from 'styled-components';
import {AccentColorOptions} from 'epic-ue-theme';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import root from 'window-or-global';

interface FeaturesCarouselTrackProps {
    slides: FeaturesCarouselSlideProps[];
    tabMenuItemClick: (newIndex: any) => void;
    slideIndex: number;
    accentColor?: AccentColorOptions;
}

const StyledTabs = styled(Tabs)<{accentcolor?: AccentColorOptions}>`
    && {
        position: relative;
        min-height: unset;

        .scroll-buttons {
            svg {
                font-size: 1.5rem;
            }
            box-sizing: unset;
            position: absolute;
            width: 24px;
            top: 0;
            height: 51px;
            z-index: 100;
            opacity: 1;
            transition: opacity 0.2s ${(p) => p.theme.easing};
            color: ${(p) => (p.accentcolor ? p.theme.palette.accent[p.accentcolor] : 'inherit')};
            padding-top: 5px;
        }

        .scroll-buttons:first-child {
            left: 0;
            top: 1px;
            background: linear-gradient(
                90deg,
                ${(p) => p.theme.palette.background.default} 0%,
                rgba(32, 35, 55, 0) 100%
            );
            padding-right: 22px;
            padding-left: 10px;
        }

        .scroll-buttons:last-child {
            right: 0;
            top: 1px;
            background: linear-gradient(
                270deg,
                ${(p) => p.theme.palette.background.default} 0%,
                rgba(32, 35, 55, 0) 100%
            );
            padding-right: 10px;
            padding-left: 22px;
        }

        .scroll-buttons-disabled {
            opacity: 0;
        }
    }
`;

const StyledTab = styled(Tab)<{accentcolor?: AccentColorOptions}>`
    && {
        color: ${(p) => p.theme.palette.text.hint};
        transition: color 0.2s ${(p) => p.theme.easing};
        min-width: unset;
        border-top: 1px solid ${(p) => p.theme.palette.text.hint};
        height: 56px;
        display: block;
        opacity: 1;
        padding-left: 16px;
        padding-right: 16px;
        max-width: unset;
        border-radius: 0;
        border-bottom: none;

        &::before {
            content: '';
            top: -18px;
            position: relative;
            display: block;
            border-top: 4px solid transparent;
            transition: border-color 0.2s ${(p) => p.theme.easing};
        }

        &.Mui-selected { color: ${(p) => p.theme.palette.text.hint}}

        &.active {
            color: ${(p) => p.theme.palette.text.primary};
        }

        &.active::before {
            border-color: ${(p) =>
                p.accentcolor ? p.theme.palette.accent[p.accentcolor] : 'inherit'};
        }

        &:hover {
            color: ${(p) => p.theme.palette.text.primary};
            background-color: transparent;
        }

        &:first-child {
            padding-left: 0;
        }

        &:last-child {
            padding-right: 0;
        }

        &:focus-visible {
            span {
                outline: 1px solid ${(p) => p.theme.palette.misc.outline};
                outline-offset: -1px;
            }
        }

       

        span {
            font-family: ${(p) => p.theme.fonts.brutal.regular};
            font-weight: 500;
            letter-spacing: 2px;
            font-size: 13px;
            line-height: 16px;
            text-transform: uppercase;
            margin: 0;
        }
    }
`;

export const FeaturesCarouselTrack = ({
    slides = [],
    slideIndex,
    tabMenuItemClick,
    accentColor
}: FeaturesCarouselTrackProps): JSX.Element | null => {
    const [slideNum, setSlideNum] = useState(0);
    const [elRefs, setElRefs] = useState<RefObject<HTMLDivElement>[]>([]);
    const tabsRef = useRef<HTMLButtonElement>(null);
    const [variant, setVariant] = useState<'standard' | 'scrollable' | 'fullWidth'>('scrollable');
    const numSlides = slides.length;

    useEffect(() => {
        setElRefs((elRefs) =>
            Array(numSlides)
                .fill(null)
                .map((_, i) => elRefs[i] || createRef())
        );
    }, [numSlides]);

    useEffect(() => {
        const handleResize = () => {
            let width = 0;
            elRefs.forEach((ref) => {
                width += ref?.current?.offsetWidth || 0;
            });
            const containerWidth = tabsRef?.current?.offsetWidth || 0;
            setVariant(containerWidth >= width ? 'standard' : 'scrollable');
        };

        handleResize();
        root.addEventListener('resize', handleResize);
        return () => root.removeEventListener('resize', handleResize);
    }, [elRefs, tabsRef]);

    const stopFocusOnClick = useCallback((e) => {
        e.preventDefault();
    }, []);

    const handleKeyUp = useCallback(
        (i) => (e) => {
            // Enter = 12, Space = 32
            if (e.keyCode === 13 || e.keyCode === 32) {
                tabMenuItemClick(i);
                setSlideNum(i);
            }
        },
        [setSlideNum]
    );

    if (numSlides < 2 || !slides.reduce((prev, curr) => prev + (curr.slideName || ''), ''))
        return null;

    return (
        <StyledTabs
            ref={tabsRef}
            value={slideNum}
            variant={variant}
            TabIndicatorProps={{
                style: {
                    display: 'none'
                }
            }}
            TabScrollButtonProps={{
                classes: {
                    disabled: 'scroll-buttons-disabled'
                }
            }}
            accentcolor={accentColor}
            centered={variant !== 'scrollable'}
            classes={{
                scrollButtons: 'scroll-buttons'
            }}>
            {slides.map((slide = {}, i) => {
                const active = i === slideIndex;
                const {slideName, title} = slide;
                return (
                    <StyledTab
                        ref={elRefs[i]}
                        accentcolor={accentColor}
                        tabIndex={0}
                        onClick={() => tabMenuItemClick(i)}
                        onMouseDown={stopFocusOnClick}
                        onKeyUp={handleKeyUp(i)}
                        className={`slide-name ${active ? 'active' : ''}`}
                        key={`f-track-slide-${slideName || title}-${i}`}
                        label={slideName}
                    />
                );
            })}
        </StyledTabs>
    );
};
